import React from 'react';
import styled from 'styled-components';
import { useFormContext, Controller } from 'react-hook-form';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Input as BaseInput, Form, Message, Icon, Label as BaseLabel } from 'semantic-ui-react';
import { isValidNumber } from 'libphonenumber-js';
import { PhoneInput, FormLabel as BaseFormLabel } from '../../../../components';

import styles from '../../../../styles';

const FormLabel = styled(BaseFormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const Input = styled(BaseInput)`
    & input {
        background-color: ${props =>
            props.readOnly ? '#fffaf3' : '#fff'} !important;
    }
`;

const intlMessages = defineMessages({
    firstNameError: {
        id: 'pages.administrators.form.firstNameError',
        defaultMessage: 'Enter user first name',
    },
    emailError: {
        id: 'pages.administrators.form.emailError',
        defaultMessage: 'Enter user email',
    },
    passwordError: {
        id: 'pages.administrators.form.passwordError',
        defaultMessage: 'Enter user password',
    },
    passwordRepeatError: {
        id: 'pages.administrators.form.passwordRepeatError',
        defaultMessage: 'Confirm user password',
    },
    passwordDontMatchError: {
        id: 'pages.administrators.form.passwordDontMatchError',
        defaultMessage: 'Passwords dont match',
    },
    phoneError: {
        id: 'pages.administrators.form.phoneError',
        defaultMessage: 'Invalid phone number.',
    },
    noCountryCodeError: {
        id: 'pages.administrators.form.noCountryCodeError',
        defaultMessage: 'Phone number should start with country code',
    },
});

function AdministratorForm({ readOnly, mode, intl }) {
    const { control, watch, formState: { errors } } = useFormContext();

    const validatePhone = value => {
        if (!value) return true;
        if (value[0] !== '+') return intl.formatMessage(intlMessages.noCountryCodeError);
        if (!isValidNumber(value)) return intl.formatMessage(intlMessages.phoneError);
        return true;
    };

    return (
        <Form error={!!errors.root}>
            {errors.root && (
                <Message error>
                    <Icon name="warning sign" />
                    {errors.root.message}
                </Message>
            )}
            <Form.Field error={!!errors.firstName}>
                <FormLabel>
                    <FormattedMessage
                        id="pages.administrators.form.firstName"
                        defaultMessage="First Name"
                    />
                </FormLabel>
                <Controller
                    name="firstName"
                    control={control}
                    rules={{ required: intl.formatMessage(intlMessages.firstNameError) }}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Input {...field} readOnly={readOnly} fluid />
                    )}
                />
                {errors.firstName && (
                    <BaseLabel color="red" pointing basic>
                        {errors.firstName.message}
                    </BaseLabel>
                )}
            </Form.Field>
            <Form.Field>
                <FormLabel>
                    <FormattedMessage
                        id="pages.administrators.form.lastName"
                        defaultMessage="Last Name"
                    />
                </FormLabel>
                <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Input {...field} readOnly={readOnly} fluid />
                    )}
                />
            </Form.Field>
            <Form.Field error={!!errors.email}>
                <FormLabel>
                    <FormattedMessage
                        id="pages.administrators.form.email"
                        defaultMessage="E-mail"
                    />
                </FormLabel>
                <Controller
                    name="email"
                    control={control}
                    rules={{ required: intl.formatMessage(intlMessages.emailError) }}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Input {...field} readOnly={readOnly} fluid />
                    )}
                />
                {errors.email && (
                    <BaseLabel color="red" pointing basic>
                        {errors.email.message}
                    </BaseLabel>
                )}
            </Form.Field>
            <Form.Field error={!!errors.phone}>
                <FormLabel>
                    <FormattedMessage
                        id="pages.administrators.form.phone"
                        defaultMessage="Phone number"
                    />
                </FormLabel>
                <Controller
                    name="phone"
                    control={control}
                    rules={{ validate: validatePhone }}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <PhoneInput
                            {...field}
                            onChange={(e, data) => field.onChange(data?.value ?? e)}
                            disabled={readOnly}
                        />
                    )}
                />
                {errors.phone && (
                    <BaseLabel color="red" pointing basic>
                        {errors.phone.message}
                    </BaseLabel>
                )}
            </Form.Field>
            <Form.Field error={!!errors.password}>
                <FormLabel>
                    <FormattedMessage
                        id="pages.administrators.form.password"
                        defaultMessage="Password"
                    />
                </FormLabel>
                <Controller
                    name="password"
                    control={control}
                    rules={{
                        required: mode === 'add'
                            ? intl.formatMessage(intlMessages.passwordError)
                            : false,
                    }}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Input {...field} type="password" readOnly={readOnly} fluid />
                    )}
                />
                {errors.password && (
                    <BaseLabel color="red" pointing basic>
                        {errors.password.message}
                    </BaseLabel>
                )}
            </Form.Field>
            <Form.Field error={!!errors.passwordRepeat}>
                <FormLabel>
                    <FormattedMessage
                        id="pages.administrators.form.passwordRepeat"
                        defaultMessage="Repeat password"
                    />
                </FormLabel>
                <Controller
                    name="passwordRepeat"
                    control={control}
                    rules={{
                        required: mode === 'add'
                            ? intl.formatMessage(intlMessages.passwordRepeatError)
                            : false,
                        validate: value => {
                            const password = watch('password');
                            if (!value && !password) return true;
                            if (value !== password) return intl.formatMessage(intlMessages.passwordDontMatchError);
                            return true;
                        },
                    }}
                    render={({ field: { ref: _ref, ...field } }) => (
                        <Input {...field} type="password" readOnly={readOnly} fluid />
                    )}
                />
                {errors.passwordRepeat && (
                    <BaseLabel color="red" pointing basic>
                        {errors.passwordRepeat.message}
                    </BaseLabel>
                )}
            </Form.Field>
        </Form>
    );
}

export default injectIntl(AdministratorForm);
